// pipe: the PURE text matcher behind the shared ERP searchable menu (the Vitest seam — a sibling of
// erp-table-filter.ts / humanize.ts). No React, no styling: one job, turning what the user typed into
// the options it should narrow to.
//
// Matching is deliberately forgiving, because the things ERP users type are not the things the
// catalogue stores: a report is called "Mass Balance" on screen and `mass_balance_facility` on the
// wire, a customer is written "L'Oréal" and typed "loreal", a job is "EWG-067" and typed "ewg067".
// So both sides are folded to the same comparable form — accents stripped, case dropped, punctuation
// and whitespace removed — and the query is matched as TOKENS, every one of which must appear
// somewhere in the option (order-independent: "register bol" finds "BOL Register").
//
// It operates ONLY on already-redacted, display-ready values: the server has authorized + redacted
// every row before it reaches the client, so a masked title simply matches on its localized
// "Redacted (1234abcd)" label. Client narrowing is a viewing convenience, NEVER a security boundary.

/** One pickable option. `keywords` widen the haystack without being rendered (a row's visible data
 *  values — a job number or serial that lives in `data`, not in the title). */
export interface SearchableOption {
  id: string;
  label: string;
  /** A dim second line under the label — rendered AND matched (e.g. a report's server key). */
  description?: string;
  /** Extra searchable text: matched, never rendered. */
  keywords?: readonly string[];
}

/** How many options a searchable menu renders at once. The source can run to thousands (the
 *  /records first page plus whatever a server-side widen merged in) and rendering them all stalls
 *  the menu — so the list is capped and the overflow is STATED, never silently dropped. */
export const SEARCHABLE_MENU_LIMIT = 50;

// A shared global regex is safe here: `String.replace`/`String.split` do not carry `lastIndex`
// between calls (only `test`/`exec` do, which this module never uses).
const DIACRITICS = /[\u0300-\u036f]/gu;
const NON_ALPHANUMERIC = /[^\p{L}\p{N}]+/gu;

/** Fold one string to its comparable form: decomposed, accent-stripped, lower-cased, and with every
 *  separator/punctuation character removed — so "Mass-Balance", "mass balance" and "massbalance"
 *  all collapse to the same thing. */
export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase()
    .replace(NON_ALPHANUMERIC, '');
}

/** Split a query into normalized tokens. Splitting BEFORE folding is what keeps a multi-word query
 *  order-independent; folding after keeps each token accent/case-insensitive. An empty (or
 *  punctuation-only) query yields no tokens, which matches everything. */
export function searchTokens(query: string): string[] {
  return query
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .split(NON_ALPHANUMERIC)
    .map(normalizeSearchText)
    .filter(token => token.length > 0);
}

/** An option with its haystack pre-folded. The component computes these ONCE per source change so a
 *  keystrokes reuse those values for substring and bounded one-edit comparisons. */
export interface NormalizedOption<T> {
  option: T;
  /** The folded label, description and keywords — kept SEPARATE so a token can never match across
   *  two fields (a "bol" + "register" pair must not conjure a match for "lregi"). */
  parts: string[];
  /** Only human-readable labels may use typo tolerance; hidden keywords remain strict. */
  words?: string[];
}

export function normalizeOptions<T extends SearchableOption>(
  options: readonly T[]
): NormalizedOption<T>[] {
  return options.map(option => {
    const parts: string[] = [];
    for (const raw of [
      option.label,
      option.description ?? '',
      ...(option.keywords ?? []),
    ]) {
      const folded = normalizeSearchText(raw);
      if (folded) parts.push(folded);
    }
    const words = [option.label, option.description ?? '']
      .filter(value => !/\p{N}/u.test(value))
      .flatMap(searchTokens);
    return { option, parts, words };
  });
}

/** One edit, including an adjacent transposition. See the server's pipe_search_near and
 * the shared acceptance fixtures. Numbers and short words are always strict. */
export function isNearSearchWord(a: string, b: string): boolean {
  const x = Array.from(a),
    y = Array.from(b);
  if (
    x.length < 4 ||
    x.length > 64 ||
    y.length < 3 ||
    Math.abs(x.length - y.length) > 1 ||
    !/^\p{L}+$/u.test(a) ||
    !/^\p{L}+$/u.test(b)
  )
    return false;
  let i = 0;
  while (i < Math.min(x.length, y.length) && x[i] === y[i]) i++;
  if (x.length === y.length)
    return (
      x.slice(i + 1).join('') === y.slice(i + 1).join('') ||
      (x[i] === y[i + 1] &&
        x[i + 1] === y[i] &&
        x.slice(i + 2).join('') === y.slice(i + 2).join(''))
    );
  return x.length > y.length
    ? x.slice(i + 1).join('') === y.slice(i).join('')
    : x.slice(i).join('') === y.slice(i + 1).join('');
}

export function searchMatchScore<T>(
  n: NormalizedOption<T>,
  tokens: readonly string[]
): number {
  if (!tokens.length) return 300;
  const phrase = tokens.join('');
  if (n.parts.includes(phrase)) return 500;
  if (n.parts.some(p => p.startsWith(phrase))) return 400;
  let score = 300;
  for (const token of tokens) {
    if (n.parts.some(p => p.includes(token))) continue;
    if (score === 100 || !n.words?.some(w => isNearSearchWord(token, w)))
      return 0;
    score = 100;
  }
  return score;
}

/** Exact values, prefixes, token matches, then labelled near matches. Stable within each rank. */
export function matchNormalized<T>(
  normalized: readonly NormalizedOption<T>[],
  tokens: readonly string[]
): T[] {
  return normalized
    .map(n => ({ option: n.option, score: searchMatchScore(n, tokens) }))
    .filter(n => n.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(n => n.option);
}

/** One-shot narrow — the pure call sites and the tests. The component splits it in two (normalize
 *  once, match per keystroke) instead of calling this. */
export function filterSearchableOptions<T extends SearchableOption>(
  options: readonly T[],
  query: string
): T[] {
  return matchNormalized(normalizeOptions(options), searchTokens(query));
}
